import { useEffect, useState } from 'react';
import {
  useForm,
  useWatch,
  FormProvider,
  type FieldErrors,
} from 'react-hook-form';
import { createSearchParams, useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from '@/components/ui/button';
import PageShell from '@/components/PageShell';
import PageCTA from '@/components/PageCTA';
import GiftDetailsForm from '@/components/forms/GiftDetails/GiftDetailsForm';
import {
  UserSchema,
  defaultValues,
  type FormValues,
} from '@/components/forms/GiftDetails/schema';
import {
  Copyright as LandingCopyright,
  GiftSummaryChip,
  PartnerBar,
} from '@/components/LandingContext';
import useIsOnline from '@/hooks/useIsOnline';
import { useGiftDetailsMutation } from '@/tsq/mutations';
import {
  useHydrated,
  useAmount,
  useChildFirstName,
  useContributionId,
  useGiftInviteCode,
  useGiftImageUrl,
  useShowChildPhoto,
  useLegalFirstName,
  useLegalLastName,
  useNameKnownAs,
  useEmail,
  usePhone,
  usePhoneCountry,
  useRelationshipToChild,
  useSetContributorDetails,
} from '@/store';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters/currency';
import {
  DEFAULT_PHONE_COUNTRY,
  isPhoneCountry,
  splitE164,
  toE164,
} from '@/utils/helpers/phone.helpers';
import {
  mapDetailsApiErrors,
  DETAILS_FIELDS,
} from '@/utils/helpers/details-errors.helpers';
import {
  normalizeAndSanitizeString,
  normalizeEmail,
} from '@/utils/formatters/string';
import { miaConsoleLog } from '@/utils/mia-logger';
import { track } from '@/analytics';

const PRIVACY_POLICY_URL =
  import.meta.env.VITE_PRIVACY_POLICY_URL ||
  'https://legal.miawealth.co.uk/mia-wealth-privacy-and-cookies-policy.pdf';

function DownloadNote({
  childFirstName,
  className,
}: {
  childFirstName: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-2.5 text-left', className)}>
      <svg
        width='17'
        height='17'
        viewBox='0 0 24 24'
        fill='none'
        stroke='#6b736e'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
        className='mt-px shrink-0'
      >
        <path d='M12 3v12M7 10l5 5 5-5M5 21h14' />
      </svg>
      <p className='text-[13px] leading-[1.55] text-[#5a635e]'>
        We'll link this gift to your email. At the end, you can download Mia to
        keep track of your gifts to {childFirstName || 'them'}.
      </p>
    </div>
  );
}

function GiftDetails() {
  const isHydrated = useHydrated();
  const isOnline = useIsOnline();
  const navigate = useNavigate();

  // ── Store hooks — ALL before any early return ──────────────────────────────

  const giftInviteCode = useGiftInviteCode();
  const contributionId = useContributionId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const photoSignedUrl = useGiftImageUrl();
  const showChildPhoto = useShowChildPhoto();

  const savedLegalFirstName = useLegalFirstName();
  const savedLegalLastName = useLegalLastName();
  const savedNameKnownAs = useNameKnownAs();
  const savedEmail = useEmail();
  const savedPhone = usePhone();
  const savedPhoneCountry = usePhoneCountry();
  const savedRelationshipToChild = useRelationshipToChild();

  const setContributorDetails = useSetContributorDetails();

  // ── Mutations — before any early return ───────────────────────────────────

  const { mutateAsync: mutateGiftDetails } = useGiftDetailsMutation();

  // ── Form — before any early return ────────────────────────────────────────

  // A submitted number is stored in E.164; split it back into country +
  // national display. An unsubmitted draft is stored as typed with its country.
  const savedPhoneSplit = splitE164(savedPhone);
  const initialPhone = savedPhoneSplit?.national ?? savedPhone ?? '';
  const initialPhoneCountry =
    savedPhoneSplit?.country ??
    (savedPhoneCountry && isPhoneCountry(savedPhoneCountry)
      ? savedPhoneCountry
      : DEFAULT_PHONE_COUNTRY);

  const methods = useForm<FormValues>({
    resolver: zodResolver(UserSchema),
    // Validate a field once it has been left, then live as it is corrected
    // (AC 5: the state clears as soon as the input is valid).
    mode: 'onTouched',
    reValidateMode: 'onChange',
    // Focus is moved explicitly in DOM order (onInvalid / 400 mapping); RHF's
    // own focusing follows registration order, which the relationship
    // Controller would put last.
    shouldFocusError: false,
    defaultValues: {
      ...defaultValues,
      legalFirstName: savedLegalFirstName || '',
      legalLastName: savedLegalLastName || '',
      nameKnownAs: savedNameKnownAs || '',
      email: savedEmail || '',
      phone: initialPhone,
      phoneCountry: initialPhoneCountry,
      relationshipToChild: savedRelationshipToChild || '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    setError,
    setFocus,
  } = methods;

  const watchedValues = useWatch({ control });

  // ── Local state — before any early return ─────────────────────────────────

  const [showRequired, setShowRequired] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Effects — before any early return ─────────────────────────────────────

  // Draft-sync: keep the persisted store in step with what is typed so the
  // values survive a back-navigation and forward again (AC 10).
  useEffect(() => {
    setContributorDetails({
      legalFirstName: watchedValues.legalFirstName ?? '',
      legalLastName: watchedValues.legalLastName ?? '',
      email: watchedValues.email ?? '',
      phone: watchedValues.phone ?? '',
      phoneCountry: watchedValues.phoneCountry ?? DEFAULT_PHONE_COUNTRY,
      relationshipToChild: watchedValues.relationshipToChild ?? '',
      nameKnownAs: watchedValues.nameKnownAs ?? '',
    });
  }, [watchedValues, setContributorDetails]);

  useEffect(() => {
    if (!isHydrated) return;
    if (giftInviteCode && contributionId) return;

    if (!giftInviteCode) {
      console.error('Missing giftInviteCode in GiftDetails component');
      // About Us renders this, so it replaces the toast rather than needing one.
      navigate('/about-us', {
        replace: true,
        state: {
          message:
            "We couldn't find your gift link. Please open the link you were sent and we'll pick up from there.",
        },
      });
      return;
    }

    // Straight back to the amount step, which explains itself.
    console.error('Missing contributionId in GiftDetails component');
    navigate(
      {
        pathname: '/',
        search: `?${createSearchParams({ code: giftInviteCode })}`,
      },
      { replace: true },
    );
  }, [giftInviteCode, contributionId, navigate, isHydrated]);

  // ── Early return — after ALL hooks ────────────────────────────────────────

  if (!contributionId || !giftInviteCode) return null;

  // ── Submit ─────────────────────────────────────────────────────────────────

  const onInvalid = (errors: FieldErrors<FormValues>) => {
    // Outline every empty required field and move focus to the first field
    // in error (RHF focuses the first registered field with an error).
    setShowRequired(true);
    const firstInvalid = DETAILS_FIELDS.find((f) => errors[f]);
    if (firstInvalid) setFocus(firstInvalid);
  };

  const onSubmit = async (data: FormValues) => {
    setFormError(null);

    const phoneCountry = isPhoneCountry(data.phoneCountry)
      ? data.phoneCountry
      : DEFAULT_PHONE_COUNTRY;

    let payload: Omit<typeof defaultValues, 'phoneCountry'>;
    try {
      payload = {
        legalFirstName: normalizeAndSanitizeString(data.legalFirstName),
        legalLastName: normalizeAndSanitizeString(data.legalLastName),
        nameKnownAs: normalizeAndSanitizeString(data.nameKnownAs || ''),
        relationshipToChild: normalizeAndSanitizeString(
          data.relationshipToChild,
        ),
        email: normalizeEmail(data.email),
        // E.164 (AC 4); the schema guarantees the number parses when present.
        phone: toE164(data.phone, phoneCountry) ?? '',
      };
    } catch (err) {
      console.error('Error normalizing/sanitizing form data:', err);
      setFormError(
        "We couldn't read one of those details. Please check them and try again.",
      );
      return;
    }

    try {
      await mutateGiftDetails({
        code: giftInviteCode,
        uuid: contributionId,
        privacyConsent: true,
        marketingOptIn: true,
        ...payload,
      });
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      const message = err instanceof Error ? err.message : String(err);

      // A 400 is a validation failure: put each message under its field
      // instead of toasting (AC 9).
      if (status === 400) {
        const { fields, unmapped } = mapDetailsApiErrors(message);
        const mapped = Object.entries(fields) as Array<
          [keyof FormValues, string]
        >;
        mapped.forEach(([field, msg]) =>
          setError(field, { type: 'server', message: msg }),
        );
        if (unmapped.length) setFormError(unmapped.join('. '));
        if (mapped.length) {
          setShowRequired(true);
          setFocus(mapped[0][0]);
        }
        miaConsoleLog('warn', {
          event: 'gift_details_validation_error',
          contributionId,
          fields: mapped.map(([f]) => f),
          unmapped,
        });
        return;
      }

      console.error('Error submitting gift details mutation:', err);
      setFormError(
        "We couldn't save your details just then. Please try again — nothing has been lost.",
      );
      return;
    }

    setContributorDetails({ ...payload, phoneCountry });
    setShowRequired(false);
    // Never the details themselves — only that this step was cleared.
    track('detailsSubmitted');
    // V2 order: details → Add a message (AC 8).
    navigate('/personalise-occasion');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const amountLabel = formatCurrency({ amount, precision: 0 });
  const continueReady = isValid;

  return (
    <PageShell
      variant='split'
      className='gift-details-page'
      left={
        <>
          {/*
            No back control here (GIFTWEB-39 item 8): the browser's own Back
            already returns to the amount step, and typed values survive the
            round trip because `setContributionId` keeps the contributor's
            details (GIFTWEB-16).
          */}
          {/*
            text-left is explicit: the heading used to be a flex item beside
            the arrow, which sized it to its content. As a bare block it would
            otherwise inherit the mobile shell's centring and sit apart from
            the left-aligned summary chip and form below it.
          */}
          <h1 className='text-left text-[27px] leading-tight font-bold tracking-[-0.02em] text-[#1a201d] lg:text-[31px] lg:tracking-[-0.025em]'>
            Your details
          </h1>

          <GiftSummaryChip
            firstName={childFirstName}
            photoUrl={photoSignedUrl}
            showPhoto={showChildPhoto}
            amountLabel={amountLabel}
          />

          <div className='hidden lg:block'>
            <DownloadNote childFirstName={childFirstName} />
            <PartnerBar variant='desktop' />
            <LandingCopyright marginTop={12} />
          </div>
        </>
      }
      right={
        <>
          <FormProvider {...methods}>
            <GiftDetailsForm
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              childFirstName={childFirstName}
              showRequired={showRequired}
            />
          </FormProvider>

          <DownloadNote childFirstName={childFirstName} className='lg:hidden' />

          {formError && (
            <p
              role='alert'
              data-testid='gift-details-form-error'
              className='rounded-[13px] border border-[#fecdd3] bg-[#fff1f2] px-3.5 py-3 text-left text-[12.5px] leading-[1.45] text-[#9f1239]'
            >
              {formError}
            </p>
          )}

          <PageCTA>
            <p className='text-center text-xs leading-normal text-[#6b736e]'>
              By proceeding, you agree to our{' '}
              <a
                href={PRIVACY_POLICY_URL}
                className='font-bold text-[#256956] underline'
                target='_blank'
                rel='noopener noreferrer'
              >
                Privacy Policy
              </a>{' '}
              and to receiving marketing communications.
            </p>
            <Button
              className={cn(
                'w-full',
                !continueReady &&
                  'border-[1.5px] border-[#b7cabf] bg-[#dde8e2] text-[#6f8479] shadow-none hover:bg-[#dde8e2]',
              )}
              type='submit'
              form='gift-details-form'
              disabled={!isOnline || isSubmitting}
              // Muted look while the form is incomplete, but never disabled:
              // pressing it is what triggers the required-field nudge (AC 4).
              data-ready={continueReady}
              data-testid='gift-details-continue'
            >
              <span>Continue</span>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
                className='size-4.5!'
              >
                <path d='M5 12h14M13 6l6 6-6 6' />
              </svg>
            </Button>
            <div className='lg:hidden'>
              <LandingCopyright marginTop={0} />
            </div>
          </PageCTA>
        </>
      }
    />
  );
}

export default GiftDetails;
